import type { UserRoles } from "@/api/custom_types";
import { currentUserAtom } from "@/store/login";
import { redirect } from "@tanstack/react-router";
import { getDefaultStore } from "jotai";
import { z } from "zod";

/**
 * Zod schema for LoginOutput - matches the API type
 */
const UserRolesSchema = z.enum(["ADMIN", "PO", "MARKETER", "FC_USER"]);

const LoginOutputSchema = z.object({
	id: z.string(),
	username: z.string(),
	user_role: UserRolesSchema,
	need_follow_otp: z.boolean(),
});

/**
 * Roles allowed to access this application
 */
const ALLOWED_APP_ROLES: readonly UserRoles[] = ["ADMIN", "MARKETER"] as const;

/**
 * Safely parse JSON string with Zod validation
 */
function parseStoredUser(json: string) {
	return LoginOutputSchema.safeParse(
		(() => {
			try {
				return JSON.parse(json);
			} catch {
				return null;
			}
		})(),
	);
}

function getUser() {
	const store = getDefaultStore();
	let user = store.get(currentUserAtom);

	if (!user) {
		const storedUser = localStorage.getItem("LoggedInUser");
		if (storedUser) {
			const parsed = parseStoredUser(storedUser);
			if (parsed.success) {
				user = parsed.data;
				store.set(currentUserAtom, user);
			} else {
				localStorage.removeItem("LoggedInUser");
			}
		}
	}

	return user;
}

/**
 * Basic auth middleware - checks if user is logged in and has allowed role
 */
export function authMiddleware() {
	const user = getUser();

	if (!user) {
		return redirect({ to: "/login" });
	}

	if (!ALLOWED_APP_ROLES.includes(user.user_role)) {
		return redirect({ to: "/login" });
	}

	return true;
}

/**
 * Pre-configured middleware for ST Admin routes (path-agnostic)
 * All ST Admin routes require ADMIN role
 */
export function stAdminMiddleware() {
	const user = getUser();

	if (!user) {
		return redirect({ to: "/login" });
	}

	if (user.user_role !== "ADMIN") {
		return redirect({ to: "/dashboard" });
	}

	return true;
}

/**
 * Guest-only middleware for the login page - sends already-logged-in users
 * straight to their dashboard instead of asking them to log in again.
 */
export function guestMiddleware() {
	const user = getUser();

	if (!user || !ALLOWED_APP_ROLES.includes(user.user_role)) {
		return true;
	}

	throw redirect({
		to: user.user_role === "ADMIN" ? "/st-admin/dashboard" : "/dashboard",
	});
}
